/**
 * ブックマークデータ管理サービス
 * ブックマークの初期化・保存・読み込みなどを担う
 */

// ブックマーク1件のデフォルト値
const EMPTY_BOOKMARK_TEMPLATE = {
  name: '',
  url: '',
  iconUrl: '',
  color: '',
  customIcon: '',
  isEmpty: true
};

/**
 * ページ別のlocalStorageキーを返す
 * cloudId が指定されている場合は 'bookmarks_{cloudId}' を、
 * 未指定の場合は旧形式の 'bookmarks' をフォールバックで返す
 * @param {string} [cloudId] クラウド識別ID
 * @returns {string} localStorageキー
 */
export function getBookmarkStorageKey(cloudId) {
  return cloudId ? `bookmarks_${cloudId}` : 'bookmarks';
}

/**
 * 旧形式 'bookmarks' キーから新形式 'bookmarks_{cloudId}' キーへマイグレーションする
 * すでに新形式キーが存在する場合はスキップする
 * @param {string} cloudId クラウド識別ID
 */
export function migrateBookmarkStorageKey(cloudId) {
  if (!cloudId) return;
  const newKey = getBookmarkStorageKey(cloudId);
  // 新形式のキーが既にあればマイグレーション不要
  if (localStorage.getItem(newKey)) return;
  const oldData = localStorage.getItem('bookmarks');
  if (oldData) {
    localStorage.setItem(newKey, oldData);
    // 旧キーは他のページで使われる可能性があるためここでは削除しない
  }
}

/**
 * 空のブックマーク配列を生成する
 * @param {number} tileCount タイル数
 * @returns {Array} 空ブックマークの配列
 */
export function createEmptyBookmarks(tileCount) {
  const bookmarks = [];
  for (let i = 0; i < tileCount; i++) {
    bookmarks.push({
      ...EMPTY_BOOKMARK_TEMPLATE,
      id: `tile-${i + 1}`
    });
  }
  return bookmarks;
}

/**
 * ブックマークデータを初期化する（ローカルストレージから取得、なければ空のデータ）
 * @param {number} tileCount タイル数
 * @param {string} [cloudId] クラウド識別ID（ページ別ストレージ用）
 * @returns {Array} ブックマークの配列
 */
export function initializeBookmarks(tileCount, cloudId) {
  const key = getBookmarkStorageKey(cloudId);
  let storedBookmarks = localStorage.getItem(key);
  // ページ別キーにデータがなく旧形式にデータがある場合はフォールバック
  if (!storedBookmarks && cloudId) {
    storedBookmarks = localStorage.getItem('bookmarks');
  }
  if (storedBookmarks) {
    try {
      const parsedBookmarks = JSON.parse(storedBookmarks);
      // すべての id を文字列型に変換し、ID がない場合はインデックスベースで生成
      const normalized = parsedBookmarks.map((bookmark, index) => ({
        ...bookmark,
        id: bookmark.id ? String(bookmark.id) : `tile-${index + 1}`
      }));
      // タイル数が不足している場合は空タイルで補完
      while (normalized.length < tileCount) {
        normalized.push({
          ...EMPTY_BOOKMARK_TEMPLATE,
          id: `tile-${normalized.length + 1}`
        });
      }
      return normalized;
    } catch (e) {
      console.error('ブックマークデータのパースに失敗しました:', e);
      return createEmptyBookmarks(tileCount);
    }
  }
  return createEmptyBookmarks(tileCount);
}

/**
 * ブックマーク配列を正規化する（id を文字列型に統一）
 * @param {Array} bookmarks ブックマーク配列
 * @returns {Array} 正規化されたブックマーク配列
 */
export function normalizeBookmarks(bookmarks) {
  return bookmarks.map((bookmark, index) => ({
    ...bookmark,
    id: bookmark.id ? String(bookmark.id) : `tile-${index + 1}`
  }));
}

/**
 * ブックマークをローカルストレージに保存する
 * @param {Array} bookmarks ブックマーク配列
 * @param {string} [cloudId] クラウド識別ID（ページ別ストレージ用）
 */
export function saveBookmarksToStorage(bookmarks, cloudId) {
  const data = JSON.stringify(bookmarks);
  const key = getBookmarkStorageKey(cloudId);
  localStorage.setItem(key, data);
  // 後方互換: 旧形式キーも同時に更新（単一ページ利用者向け）
  localStorage.setItem('bookmarks', data);
}

/**
 * ローカルストレージからブックマークを取得する（生の配列）
 * @param {string} [cloudId] クラウド識別ID（ページ別ストレージ用）
 * @returns {Array|null} ブックマーク配列、存在しない場合は null
 */
export function loadBookmarksFromStorage(cloudId) {
  const key = getBookmarkStorageKey(cloudId);
  let stored = localStorage.getItem(key);
  // ページ別キーにデータがなく旧形式にデータがある場合はフォールバック
  if (!stored && cloudId) {
    stored = localStorage.getItem('bookmarks');
  }
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (e) {
    console.error('ローカルストレージのデータパースに失敗しました:', e);
    return null;
  }
}

// デフォルトカラーパレット（初回起動時の初期値）
const DEFAULT_PALETTE_COLORS = [
  '#A7C6E2', '#B7D9BB', '#F3B6B1', '#F3D298', '#D2D2D2', '#E8A89E'
];

/**
 * カスタムカラーをローカルストレージに保存する
 * @param {Array<string>} colors カスタムカラー配列（HEX文字列）
 */
export function saveCustomColorsToStorage(colors) {
  localStorage.setItem('bkms_custom_colors', JSON.stringify(colors));
}

/**
 * ローカルストレージからカスタムカラーを取得する（未保存時はデフォルト6色）
 * @returns {Array<string>} カスタムカラー配列
 */
export function loadCustomColorsFromStorage() {
  const stored = localStorage.getItem('bkms_custom_colors');
  if (!stored) return [...DEFAULT_PALETTE_COLORS];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [...DEFAULT_PALETTE_COLORS];
  } catch (e) {
    console.error('カスタムカラーのパースに失敗しました:', e);
    return [...DEFAULT_PALETTE_COLORS];
  }
}

export default {
  createEmptyBookmarks,
  initializeBookmarks,
  normalizeBookmarks,
  saveBookmarksToStorage,
  loadBookmarksFromStorage,
  saveCustomColorsToStorage,
  loadCustomColorsFromStorage
};
